import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

const CHECK_INTERVAL_MS = 5 * 60_000;
const MAIN_BUNDLE_RE = /main-[A-Z0-9]+\.js/;
const RELOAD_TARGET_KEY = 'motoboy_version_reload_target';

/**
 * Detecta builds nuevos para pestañas que quedan abiertas días enteros.
 *
 * Why: una pestaña cargada antes de un deploy sigue ejecutando el bundle viejo
 * (incluidos chunks lazy ya en memoria), así que los fixes no llegan y los errores
 * siguen reportándose. Compara el main-*.js cargado con el del index.html publicado
 * y, si cambió, recarga completo en la siguiente navegación (nunca a mitad de un
 * formulario).
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
    private _document = inject(DOCUMENT);
    private _router = inject(Router);

    private _loadedBundle: string | null = null;
    private _updateAvailable = false;
    private _lastCheck = 0;

    start(): void {
        const win = this._document.defaultView;
        if (!win) return;

        // En `ng serve` el bundle no lleva hash → no hay nada que comparar
        this._loadedBundle = this.currentBundle();
        if (!this._loadedBundle) return;

        win.setInterval(() => this.check(), CHECK_INTERVAL_MS);
        this._document.addEventListener('visibilitychange', () => {
            if (this._document.visibilityState === 'visible') this.check();
        });

        this._router.events.pipe(filter((e) => e instanceof NavigationStart)).subscribe((e: NavigationStart) => {
            if (!this._updateAvailable) return;
            win.location.assign(e.url);
        });
    }

    private readReloadTarget(): string | null {
        try {
            return sessionStorage.getItem(RELOAD_TARGET_KEY);
        } catch {
            return null;
        }
    }

    private writeReloadTarget(bundle: string): void {
        try {
            sessionStorage.setItem(RELOAD_TARGET_KEY, bundle);
        } catch {
            // Storage bloqueado: sin anti-loop persistente, igual se recarga una vez por pestaña
        }
    }

    private currentBundle(): string | null {
        const scripts = Array.from(this._document.querySelectorAll<HTMLScriptElement>('script[src]'));
        for (const s of scripts) {
            const match = s.getAttribute('src')?.match(MAIN_BUNDLE_RE);
            if (match) return match[0];
        }
        return null;
    }

    private async check(): Promise<void> {
        if (this._updateAvailable || Date.now() - this._lastCheck < 60_000) return;
        this._lastCheck = Date.now();
        try {
            const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
            if (!res.ok) return;
            const published = (await res.text()).match(MAIN_BUNDLE_RE)?.[0];
            if (published && published !== this._loadedBundle) {
                // Anti-loop: si ya recargamos buscando este build y seguimos en otro (CDN desfasado), no insistir
                if (this.readReloadTarget() === published) return;
                this.writeReloadTarget(published);
                this._updateAvailable = true;
                console.log('[version_check] build nuevo disponible, se recarga en la próxima navegación', {
                    loaded: this._loadedBundle,
                    published,
                });
            }
        } catch {
            // Sin red o CDN caído: se reintenta en el próximo ciclo
        }
    }
}
